import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import { installCursorAutoHide } from './lib/cursor'
import { initSentry } from './lib/sentry'
import './index.css'
import { reportBuild } from './lib/reportBuild'

// Live version reporting for the admin Ops pages (see reportBuild.ts).
reportBuild('webplayer')

// Init Sentry first so it can capture errors during the rest of bootstrap
// (cursor install, root creation, even the first render). No-op when the
// VITE_SENTRY_DSN_WEBPLAYER env var is unset.
initSentry()

// Kick off the cursor auto-hide *before* the React tree mounts so the
// pointer is hidden from the very first paint, not after a frame of default.
installCursorAutoHide()

// PWA service worker — registered here (not via vite-plugin-pwa's injected
// registerSW.js, which has no rejection handler) so a failed registration is
// swallowed instead of surfacing as an unhandled rejection in Sentry. iOS
// Safari rejects register() in private browsing / under storage pressure;
// the player still works fully without the SW, just with no offline cache.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Recorded BEFORE register() resolves: true only if a worker was
    // already controlling this page (i.e. this is a repeat visit picking up
    // an update), false on a first-ever install. Distinguishes "a new
    // version just took over" from "the very first SW just activated" —
    // the latter is not a change worth reloading for.
    const hadController = Boolean(navigator.serviceWorker.controller)

    navigator.serviceWorker.register('/sw.js', { scope: '/' }).then(reg => {
      // Kiosks boot once and never navigate again, so the browser's own
      // background update check (in practice, roughly daily) is the only
      // thing that would otherwise notice a redeploy — a same-day SW
      // hotfix could sit unpicked-up for hours on an already-open tab.
      // Poll explicitly so a fix reaches the kiosk within minutes instead.
      window.setInterval(() => reg.update().catch(() => {}), 5 * 60_000)
    }).catch(() => {
      // Benign: no SW support/storage — run online-only.
    })

    // skipWaiting + clientsClaim (see vite.config.ts) mean a new SW takes
    // over the page without waiting for a close/reopen — but the running
    // React app is still the OLD bundle until it reloads. Reload once so
    // the kiosk actually picks up the new app shell rather than running
    // stale JS against a freshly-swapped SW/cache.
    let reloadedForNewWorker = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) return  // first-ever activation, not an update
      if (reloadedForNewWorker) return
      // Cooldown (survives the reload via sessionStorage, cleared only when
      // the tab/kiosk session ends): if the SW keeps handing off between
      // versions — e.g. a still-propagating deploy where different edge
      // nodes serve different builds — reloading on every handoff would
      // reload the kiosk in a loop, and for an unpaired screen that means
      // calling /register again on every reload, which by design rotates
      // the pairing code each time (see devices.py register_device). One
      // unstable deploy shouldn't turn into an unusable, ever-changing
      // pairing code — better to keep running the current tab and pick up
      // the update on the next cooldown window.
      const RELOAD_COOLDOWN_MS = 5 * 60_000
      const lastReloadAt = Number(sessionStorage.getItem('wt-sw-reload-at') || 0)
      if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return
      reloadedForNewWorker = true
      sessionStorage.setItem('wt-sw-reload-at', String(Date.now()))
      window.location.reload()
    })
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
)
